// src/pages/UserManagement.jsx
// Control Panel → User Management. Everyone who may use the portal, kept
// in the portal database. What each person can do here comes from their
// role (Roles & Access), and they only act on people whose role sits below
// theirs in their own group; Super Admins act on everyone. The server
// enforces the same rules.
//
// Views: Active, 2FA not set up, Reset requested, Removed, Access requests
// (people who asked from the email-to-fax warning), and Email-to-fax
// senders without an account.
import { useCallback, useEffect, useMemo, useState } from 'react';
import { apiCall } from '../components/EmailEditorParts';
import ShowSelect from '../components/ShowSelect';
import Avatar from '../components/Avatar';
import Pager, { usePager } from '../components/Pager';
import { useAuth } from '../hooks/useAuth';
import { useNotify } from '../components/Notifications';
import {
  fetchUsers, addUser, userAction, setUserRole, decideAccessRequest,
  fetchUnregisteredSenders, notifyUnregisteredSenders, remindUnregisteredSenders, signOutUsers, downloadPeopleReport,
  fetchDigestRecipients, sendUserInvite, resetUserPassword,
  fetchFollowupOwners, setFollowupOwners,
} from '../utils/api';
import { timeZone, tzLabel } from '../utils/timeZone';
import { PILL, PILL_ON, TH_SELECT, TH_OPTION } from '../theme/tabs';
import TeamFilter, { useClientTeams } from '../components/TeamFilter';
import { isSuperAdminEmail } from '../utils/account';
import Loader from '../components/Loader';
import Emo from '../components/Emo';
import { formatFax } from '../utils/format';
import { useClientWord } from '../hooks/useClientWord';

function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-US', {
    timeZone: timeZone(), month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit',
  }) + ' ' + tzLabel(new Date(iso));
}
function formatDay(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', { timeZone: timeZone(), month: 'short', day: 'numeric', year: 'numeric' });
}

const PEOPLE_VIEWS = [
  { id: 'active',  label: 'Active' },
  { id: 'online',  label: 'Online now', perm: 'users.online' },
  { id: 'invited', label: 'Invited' },
  { id: 'expired', label: 'Invite expired' },
  { id: 'no2fa',   label: '2FA not set up' },
  // 3.68.3 (owner): the Overview's "Your team" lists open these
  { id: 'never',   label: 'Never signed in' },
  { id: 'inactive', label: 'Not active 30+ days' },
  { id: 'reset',   label: 'Reset requested' },
  { id: 'removed', label: 'Removed' },
  // 3.62.18 (owner): emails to them bounced "address doesn't exist", so the system turned their access off
  { id: 'deactivated', label: 'Deactivated' },
  { id: 'blocked', label: 'Blocked' },   // 3.57.9: after 5 failed faxes to one number in a day
];

function inView(u, view) {
  const deactivated = u.status === 'removed' && u.removedReason === 'email-not-found';
  if (view === 'deactivated') return deactivated;
  if (view === 'removed') return u.status === 'removed' && !deactivated;
  if (u.status !== 'active') return false;
  if (view === 'blocked') return !!u.accessBlock;
  if (view === 'online') return !!u.online;
  if (view === 'invited') return u.inviteStatus === 'pending';
  if (view === 'expired') return u.inviteStatus === 'expired';
  if (view === 'no2fa') return !u.twoFactor;
  // (as the Overview counts them: Super Admins aren't part of a client's team)
  if (view === 'never') return u.level !== 'super_admin' && (!u.hasSignIn || !u.lastActiveAt);
  if (view === 'inactive') return u.level !== 'super_admin' && !!u.lastActiveAt && Date.parse(u.lastActiveAt) < Date.now() - 30 * 864e5;
  if (view === 'reset') return !!u.resetRequestedAt;
  return true;
}

export default function UserManagement({ initialView = null }) {
  const clientWord = useClientWord();   // 3.67.5: 'Company' for the people at a client
  const { getToken, shows, viewAs, startViewAs } = useAuth();
  const { notify, confirm } = useNotify();
  const [info, setInfo]       = useState(null);   // the /portal/users response
  const [senders, setSenders] = useState(null);   // the unregistered senders response
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');
  // The sign-in alert can open this page straight on the senders list
  const [view, setView]       = useState(() => {
    // initialView (3.34.6): opened from the Overview on a list (senders, requests, invited, no2fa…)
    if (initialView) return initialView;
    try { return window.sessionStorage.getItem('faxPortal.userMgmtView') || 'active'; } catch { return 'active'; }
  });
  useEffect(() => {
    try { window.sessionStorage.removeItem('faxPortal.userMgmtView'); } catch { /* ignore */ }
  }, []);
  const [picking, setPicking] = useState(false);   // choosing who gets the list now
  const [pwFor, setPwFor]     = useState(null);    // person whose password is being reset
  const [search, setSearch]   = useState('');
  // People list (3.18.1): pick one account (Account column), and Show 50 by
  // default like the fax lists
  const [accountPick, setAccountPick] = useState('');
  const [showN, setShowN]     = useState('50');
  const [busy, setBusy]       = useState('');
  const [adding, setAdding]   = useState(null);   // null | { email, name }
  // Sorting: click a column title (again to reverse). { key, dir: 1 | -1 }
  const [sort, setSort] = useState({ key: 'name', dir: 1 });
  const onSort = key => setSort(s => (s.key === key ? { key, dir: -s.dir } : { key, dir: 1 }));
  // People ticked for bulk actions (sign out, report); cleared when the list changes
  const [selected, setSelected] = useState(() => new Set());
  useEffect(() => { setSelected(new Set()); }, [view]);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const [u, s] = await Promise.all([fetchUsers(getToken), fetchUnregisteredSenders(getToken).catch(() => null)]);
      setInfo(u);
      setSenders(s);
    } catch (e) { setError(e.message); }
    setLoading(false);
  }, [getToken]);
  useEffect(() => { load(); }, [load]);

  const allUsers = useMemo(() => info?.data || [], [info]);
  // Accounts the people in the list belong to; the Account column becomes a
  // picker when there's more than one (e.g. Super Admins on All accounts)
  // 3.19.5: only on All clients; with one client picked, People is only
  // about that client (someone also in, e.g., Jeremy doesn't bring it in)
  const accountOptions = useMemo(() => {
    if (info?.account) return [];
    const m = new Map();
    allUsers.forEach(u => {
      if (u.group) m.set(u.group, u.groupLabel || u.group);
      (u.accounts || []).forEach(a => m.set(a.id, a.name || a.id));
    });
    return [...m.entries()].map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [allUsers, info]);
  // Team filter (3.19.5): the picked client's teams
  const clientTeams = useClientTeams();
  const [teamPick, setTeamPick] = useState('');
  const here = info?.account?.id || null;
  const teamsHere = u => ((u.accounts || []).find(a => a.id === here)?.teams || []);
  const users = useMemo(() => (!accountPick ? allUsers
    : allUsers.filter(u => u.group === accountPick || (u.accounts || []).some(a => a.id === accountPick)))
    .filter(u => !teamPick || teamsHere(u).includes(teamPick)),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [allUsers, accountPick, teamPick, here]);
  const requests = useMemo(() => info?.requests || [], [info]);
  // People whose access was removed aren't listed (3.34.8): their emails are refused anyway
  const senderRows = useMemo(() => (senders?.data || []).filter(s => !s.removed), [senders]);
  // Individual clients (3.19.12): no teams, and the owner plus up to 3
  // assistants; Add person goes once that's reached
  const individualHere = info?.account?.type === 'individual';
  const activeHere = individualHere ? allUsers.filter(u => u.status === 'active').length : 0;
  const individualFull = individualHere && activeHere >= (info?.individualMax || 2);
  const canAdd   = !!info?.canAdd && shows('users.add') && !individualFull;

  const counts = useMemo(() => ({
    ...Object.fromEntries(PEOPLE_VIEWS.map(v => [v.id, users.filter(u => inView(u, v.id)).length])),
    requests: requests.length,
    senders : senderRows.length,
  }), [users, requests, senderRows]);

  const q = search.trim().toLowerCase();
  const match = (...vals) => !q || vals.some(v => String(v || '').toLowerCase().includes(q));
  const LEVEL_ORDER = { user: 1, manager: 2, admin: 3, master_admin: 4, super_admin: 5 };
  const peopleSortValue = {
    name   : u => (u.name || u.email).toLowerCase(),
    role   : u => `${LEVEL_ORDER[u.level] || 0}${(u.roleLabel || '').toLowerCase()}`,
    twofa  : u => (u.twoFactor ? 1 : 0),
    when   : u => (view === 'removed' || view === 'deactivated' ? u.removedAt : view === 'online' ? u.onlineSince : u.lastActiveAt) || '',
  };
  const listUsers     = sortRows(users.filter(u => inView(u, view) && match(u.email, u.secondaryEmail, u.name, u.roleLabel)), peopleSortValue[sort.key], sort.dir);
  const { pageRows: shownUsers, pager } = usePager(listUsers, showN);   // page by page (3.26.0)
  const peopleViews   = PEOPLE_VIEWS.filter(v => !v.perm || shows(v.perm));
  const canBulk       = shows('users.online') || shows('users.export');
  const picked        = shownUsers.filter(u => selected.has(u.email));
  const toSignOut     = picked.filter(u => u.online && u.canSignOut);
  const allTicked     = shownUsers.length > 0 && shownUsers.every(u => selected.has(u.email));
  const toggle = email => setSelected(s => { const n = new Set(s); if (n.has(email)) n.delete(email); else n.add(email); return n; });
  const toggleAll = () => setSelected(allTicked ? new Set() : new Set(shownUsers.map(u => u.email)));

  // Sign out on every device (Online now)
  async function signOut(list) {
    const who = list.length === 1 ? (list[0].name || list[0].email) : `${list.length} people`;
    const ok = await confirm({
      title: `Sign out ${who}?`,
      message: `${list.length === 1 ? 'They are' : 'They are each'} signed out of the Fax Portal on every device within about 2 minutes, and must sign in again. Their access, faxes and settings don't change.`,
      confirmLabel: 'Sign out', danger: true,
    });
    if (!ok) return;
    setBusy('signout');
    try {
      const r = await signOutUsers(list.map(u => u.email), getToken);
      notify({ bell: 'people', tone: 'success', title: `${r.signedOut} ${r.signedOut === 1 ? 'person' : 'people'} signed out`, detail: r.skipped ? `${r.skipped} skipped: you can only sign out people below you.` : undefined });
      setSelected(new Set());
      await load();
    } catch (e) { notify({ tone: 'error', title: "Couldn't sign them out", detail: e.message }); }
    setBusy('');
  }

  // People report: the ticked people, or everyone in this list
  async function downloadReport() {
    setBusy('report');
    try {
      await downloadPeopleReport(picked.length ? picked.map(u => u.email) : listUsers.map(u => u.email), getToken);
    } catch (e) { notify({ tone: 'error', title: "Couldn't download the report", detail: e.message }); }
    setBusy('');
  }
  const shownRequests = requests.filter(r => match(r.email, r.name));
  const senderSortValue = {
    email  : s => s.email, account: s => (s.groupLabel || '').toLowerCase(), faxes: s => s.faxes || 0,
    lastFax: s => s.lastFaxAt || '', lastReminder: s => s.lastWarnedAt || '',
  };
  const shownSenders  = sortRows(senderRows.filter(s => match(s.email)), senderSortValue[sort.key] || senderSortValue.email, sort.key in senderSortValue ? sort.dir : 1);

  // "All accounts" (Super Admins) and someone in several accounts: which one?
  const askAccount = u => !info?.account && (u.accounts || []).length > 1;

  async function act(u, action) {
    const who = u.name || u.email;
    // 3.57.10 (owner): faxes held when they were blocked: send them or drop them
    if (action === 'unblock' && u.accessBlock?.held > 0) {
      const n = u.accessBlock.held, num = formatFax(u.accessBlock.number);
      const choice = await confirm({
        title: `Unblock ${who}?`,
        message: `${who} was blocked after ${u.accessBlock.count} failed faxes to ${num} in one day. ${n === 1 ? '1 more fax' : `${n} more faxes`} to ${num} ${n === 1 ? 'was' : 'were'} waiting to go out and ${n === 1 ? 'was' : 'were'} held. What should happen to ${n === 1 ? 'it' : 'them'}?`,
        choices: [
          { value: 'send', label: `Send ${n === 1 ? 'it' : 'them'} now`, hint: `Only if the number ${num} has been checked with the receiver.` },
          { value: 'drop', label: `Drop ${n === 1 ? 'it' : 'them'}`, hint: `${n === 1 ? "It isn't" : "They aren't"} sent. ${who} can send again after checking the number.` },
        ],
        confirmLabel: 'Unblock',
      });
      if (!choice) return;
      setBusy(`unblock:${u.email}`);
      try {
        await userAction(u.email, 'unblock', getToken, { held: choice });
        notify({ bell: 'people', tone: 'success', title: `${who} is unblocked`, detail: choice === 'send' ? `The held ${n === 1 ? 'fax goes' : 'faxes go'} out shortly.` : `The held ${n === 1 ? 'fax was' : 'faxes were'} dropped.` });
        await load();
      } catch (e) { notify({ tone: 'error', title: 'That didn\'t work', detail: e.message }); }
      setBusy('');
      return;
    }
    if (action === 'remove' && askAccount(u)) {
      const choice = await confirm({
        title: `Remove ${who}'s access?`,
        message: `${who} is in ${u.accounts.length} clients. Where should their access be removed?`,
        choices: [
          ...u.accounts.map(a => ({ value: a.id, label: `${a.name} only`, hint: `They keep ${u.accounts.filter(x => x.id !== a.id).map(x => `${x.name} (${x.roleLabel})`).join(', ')}.` })),
          { value: 'all', label: 'All clients', hint: "They can't sign in to the Fax Portal any more, and are signed out everywhere." },
        ],
        confirmLabel: 'Remove access', danger: true,
      });
      if (!choice) return;
      const where = choice === 'all' ? null : u.accounts.find(a => a.id === choice);
      setBusy(`remove:${u.email}`);
      try {
        await userAction(u.email, 'remove', getToken, { account: choice });
        notify({ bell: 'people', tone: 'success', title: where ? `${who} was removed from ${where.name}` : `${who} no longer has access` });
        await load();
      } catch (e) { notify({ tone: 'error', title: 'That didn\'t work', detail: e.message }); }
      setBusy('');
      return;
    }
    const prompts = {
      'reset-2fa': {
        title: `Reset two-factor authentication for ${who}?`,
        message: `Their authenticator app will stop working for the portal and they'll be signed out everywhere. At their next sign-in they'll set up a new one. We'll email ${u.email} and Fax Portal support to let them know.${u.resetRequestedAt ? '' : '\n\nThey haven\'t asked for this from the sign-in page, so please check with them first.'}`,
        confirmLabel: 'Reset 2FA', danger: true, done: `Two-factor authentication was reset for ${who}`,
      },
      remove: {
        title: `Remove ${who}'s access?`,
        message: `${u.email} won't be able to sign in to the Fax Portal, and is signed out everywhere. Their faxes and history stay. You can give access back later from the Removed list.`,
        confirmLabel: 'Remove access', danger: true, done: `${who} no longer has access`,
      },
      unblock: {
        title: `Unblock ${who}?`,
        message: `${who} was blocked after ${u.accessBlock?.count || 5} failed faxes to ${formatFax(u.accessBlock?.number)} in one day${u.accessBlock?.until ? `, until ${formatDate(u.accessBlock.until)} (access comes back by itself then)` : ''}. Please make sure they've checked the number with the receiver.\n\nOnce unblocked, they can sign in and send faxes by email again, and they're emailed that their access is restored.`,
        confirmLabel: 'Unblock', done: `${who} is unblocked`,
      },
      restore: u.removedReason === 'email-not-found' ? {
        // 3.62.18: deactivated because emails to the address bounced as not existing
        title: `Reactivate ${who}?`,
        message: `Emails to ${u.email} came back because the address didn't exist. Reactivate only once it works again: their access comes back and our emails can reach them again. Then send them an invite or a password reset.`,
        confirmLabel: 'Reactivate', done: `${who} is active again`,
      } : {
        title: `Give ${who} access again?`,
        message: `${u.email} will be able to sign in again, and must set up two-factor authentication if they haven't.`,
        confirmLabel: 'Give access', done: `${who} has access again`,
      },
    }[action];
    if (!(await confirm(prompts))) return;
    setBusy(`${action}:${u.email}`);
    try {
      await userAction(u.email, action, getToken);
      notify({ bell: 'people', tone: 'success', title: prompts.done });
      await load();
    } catch (e) { notify({ tone: 'error', title: 'That didn\'t work', detail: e.message }); }
    setBusy('');
  }

  // Role choices for a row (Super Admins have no account of their own)
  // 3.19.13: Super Admin is only offered for admin@, fax@ and support@
  const roleChoices = u => (info?.roleOptions?.[u.group] || info?.roleOptions?.[info?.account?.id] || Object.values(info?.roleOptions || {})[0] || [])
    .filter(o => o.id !== 'super_admin' || isSuperAdminEmail(u.email));

  async function changeRole(u, role) {
    const label = roleChoices(u).find(o => o.id === role)?.label || role;
    let account = null;
    if (role === 'super_admin' || u.role === 'super_admin') {
      const promote = role === 'super_admin';
      const ok = await confirm({
        title: promote ? `Make ${u.name || u.email} a Super Admin?` : `Make ${u.name || u.email} a ${label}?`,
        message: promote
          ? 'Super Admins see and change everything at every client: people, roles, billing, reminders and other Super Admins. Only do this for people who run the portal.'
          : `They stop being a Super Admin and become a ${label}${info?.account ? ` in ${info.account.name}` : ' at their own client'}.`,
        confirmLabel: promote ? 'Make Super Admin' : 'Change role', danger: promote,
      });
      if (!ok) return;
      setBusy(`role:${u.email}`);
      try {
        await setUserRole(u.email, role, getToken, info?.account?.id || null);
        notify({ bell: 'people', tone: 'success', title: promote ? `${u.name || u.email} is now a Super Admin` : `${u.name || u.email} is now a ${label}` });
        await load();
      } catch (e) { notify({ tone: 'error', title: "Couldn't change the role", detail: e.message }); }
      setBusy('');
      return;
    }
    if (askAccount(u)) {
      // 3.68.1 (owner): only the clients that have this role (Master Admin is
      // for business clients only, so an individual client is never offered)
      const fits = u.accounts.filter(a => (info?.roleOptions?.[a.id] || []).some(o => o.id === role));
      if (!fits.length) { notify({ tone: 'error', title: `${label} isn't a role at ${u.name || u.email}'s clients` }); return; }
      if (fits.length === 1) {
        if (!(await confirm({ title: `Make ${u.name || u.email} a ${label}?`, message: `This is for ${fits[0].name} (now ${fits[0].roleLabel}). What they can do there changes within a minute.`, confirmLabel: 'Change role' }))) return;
        account = fits[0].id;
      } else {
        account = await confirm({
          title: `Make ${u.name || u.email} a ${label}?`,
          message: `${u.name || u.email} has a role at ${fits.length} clients with this role. Which client is this for? What they can do there changes within a minute.`,
          choices: fits.map(a => ({ value: a.id, label: a.name, hint: `Now ${a.roleLabel}` })),
          confirmLabel: 'Change role',
        });
        if (!account) return;
      }
    } else if (!(await confirm({ title: `Make ${u.name || u.email} a ${label}?`, message: `What they can see and do changes to the ${label} role's permissions within a minute.`, confirmLabel: 'Change role' }))) return;
    setBusy(`role:${u.email}`);
    try {
      await setUserRole(u.email, role, getToken, account);
      const where = account ? ` in ${u.accounts.find(a => a.id === account)?.name}` : '';
      notify({ bell: 'people', tone: 'success', title: `${u.name || u.email} is now a ${label}${where}` });
      await load();
    } catch (e) { notify({ tone: 'error', title: "Couldn't change the role", detail: e.message }); }
    setBusy('');
  }

  // Reminders to the senders themselves (Super Admins). Not sent to people
  // whose access was removed, who already asked, or who had one today.
  const DAY_MS = 24 * 60 * 60 * 1000;
  const remindable = s => !s.removed && s.requestStatus !== 'pending' && (!s.lastWarnedAt || Date.now() - Date.parse(s.lastWarnedAt) >= DAY_MS);
  async function remind(list) {
    const due = list.filter(remindable);
    if (!due.length) { notify({ tone: 'info', title: 'Nobody is due a reminder', detail: 'Everyone here already asked for access, or had a reminder in the last 24 hours.' }); return; }
    const one = list.length === 1;
    const ok = await confirm({
      title: one ? `Email a reminder to ${due[0].email}?` : `Email a reminder to ${due.length} ${due.length === 1 ? 'person' : 'people'}?`,
      message: `They get an email saying faxes by email need a Fax Portal account, with a Request access button.${!one && due.length < list.length ? ` ${list.length - due.length} ${list.length - due.length === 1 ? 'person is' : 'people are'} skipped: already asked, or reminded in the last 24 hours.` : ''}`,
      confirmLabel: one ? 'Send reminder' : `Send ${due.length} reminder${due.length === 1 ? '' : 's'}`,
    });
    if (!ok) return;
    setBusy(one ? `remind:${due[0].email}` : 'remind:all');
    try {
      const r = await remindUnregisteredSenders(due.map(s => s.email), getToken);
      notify({ tone: r.failed ? 'error' : 'success', title: `${r.sent} reminder${r.sent === 1 ? '' : 's'} sent`, detail: r.failed ? `${r.failed} couldn't be sent. Try again in a few minutes.` : undefined });
      await load();
    } catch (e) { notify({ tone: 'error', title: "Couldn't send the reminders", detail: e.message }); }
    setBusy('');
  }

  async function decide(r, decision) {
    const who = r.name || r.email;
    // Approving: choose their role and how they'll sign in the first time
    if (decision === 'approve') {
      setAdding({ email: r.email, name: r.name || '', mode: 'approve', group: r.group || null });
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    const ok = await confirm(decision === 'approve'
      ? { title: `Give ${who} a Fax Portal account?`, message: `${r.email} joins ${r.groupLabel} as a User. We'll email them how to sign in and set up two-factor authentication.`, confirmLabel: 'Approve' }
      : { title: `Decline ${who}'s request?`, message: 'They won\'t be told. Faxes they send by email stay refused until they have a Fax Portal account.', confirmLabel: 'Decline', danger: true });
    if (!ok) return;
    setBusy(`req:${r.email}`);
    try {
      await decideAccessRequest(r.email, decision, getToken);
      notify({ bell: 'people', tone: 'success', title: decision === 'approve' ? `${who} was added` : 'Request declined' });
      await load();
    } catch (e) { notify({ tone: 'error', title: 'That didn\'t work', detail: e.message }); }
    setBusy('');
  }

  async function viewAsPerson(u) {
    try { await startViewAs(u.email); } catch (e) { notify({ tone: 'error', title: "Couldn't start View as", detail: e.message }); }
  }

  async function emailList(to) {
    setBusy('notify');
    try {
      const r = await notifyUnregisteredSenders(to, getToken);
      setPicking(false);
      notify({ bell: 'people', tone: 'success', title: r.sent ? `Sent ${r.sent} email${r.sent === 1 ? '' : 's'}` : 'Nothing to send', detail: r.sent ? undefined : 'The people you picked have nobody on their list.' });
    } catch (e) { notify({ tone: 'error', title: "Couldn't send the emails", detail: e.message }); }
    setBusy('');
  }

  const recipientsDialog = picking && (
    <RecipientsDialog getToken={getToken} busy={busy === 'notify'}
      onSend={emailList} onClose={() => setPicking(false)} />
  );

  const viewButton = (id, label, count, alert) => (
    <button key={id} role="tab" aria-selected={view === id} onClick={() => setView(id)}
      style={{ ...S.viewBtn, ...(view === id ? S.viewBtnActive : {}), ...(alert && count ? S.viewBtnAlert : {}) }}>
      {label} <span style={S.count}>{count ?? 0}</span>
    </button>
  );

  return (
    <div>
      <div style={S.topRow}>
        <div>
          <h3 style={S.title}>{individualHere ? 'Staff' : 'User Management'}</h3>
          {individualHere ? (
            <ul style={{ ...S.sub, margin: '4px 0 0', paddingLeft: 18, lineHeight: 1.7 }}>
              <li>You and <strong>one staff member</strong> can use this fax line.</li>
              <li>Each of you has your own login: no sharing passwords or two-factor codes.</li>
              <li>Two-factor authentication is required for everyone.</li>
              <li>Your staff member is added as a <strong>User</strong>.</li>
              <li>Need more people? Contact Fax Portal support to set you up as a business client.</li>
            </ul>
          ) : (
          <p style={S.sub}>
            Everyone who can sign in to the Fax Portal. Two-factor authentication is required: people without it are asked to set it up at their next sign-in.
          </p>
          )}
        </div>
        <div style={S.topActions}>
          {canAdd && <button onClick={() => setAdding(a => (a ? null : { email: '', name: '' }))} style={S.primaryBtn}>{adding ? 'Close' : '+ Add person'}</button>}
          {individualFull && <span style={S.meta} title="For more people, contact Fax Portal support to set you up as a business client">You and one staff member: the limit for an individual client ({activeHere} of {info?.individualMax || 2})</span>}
          <button onClick={load} style={S.refreshBtn}><Emo e="↻" /> Refresh</button>
        </div>
      </div>

      {adding && canAdd && (
        <AddPerson initial={adding} domains={info?.domains || []} roleOptions={info?.roleOptions || {}} account={info?.account || null}
          teamOptions={info?.teamOptions || {}} defaultTeams={info?.defaultTeams || {}}
          getToken={getToken} notify={notify}
          key={`${adding.mode || 'add'}:${adding.email}`}
          onAdded={() => { if (!adding.mode) setView('active'); setAdding(null); load(); }} />
      )}

      <div style={S.filterRow}>
        <div style={S.views} role="tablist" aria-label="Show">
          {peopleViews.map(v => viewButton(v.id, v.label, counts[v.id], v.id === 'reset' || v.id === 'expired'))}
          {canAdd && viewButton('requests', 'Access requests', counts.requests, true)}
          {senders && viewButton('senders', 'Email-to-fax without an account', counts.senders, false)}
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search name or email"
            aria-label="Search name or email" style={S.search} />
          {view !== 'requests' && view !== 'senders' && (
            <>
              <TeamFilter teams={clientTeams} value={teamPick} onChange={setTeamPick} byName
                groupStyle={S.showGroup} labelStyle={S.showLabel} style={S.showSelect} />
              <ShowSelect value={showN} onChange={setShowN} groupStyle={S.showGroup} labelStyle={S.showLabel} style={S.showSelect} />
            </>
          )}
        </div>
      </div>

      {error && <div role="alert" style={S.errorBanner}>{error}</div>}
      {recipientsDialog}
      {pwFor && <PasswordDialog person={pwFor} getToken={getToken} notify={notify} onClose={() => setPwFor(null)} />}

      {view === 'no2fa' && shows('reminders.view') && (
        <div style={S.notice}>
          <div>These people haven't set up two-factor authentication, so they can't use the portal yet. Send them a reminder from <strong>Settings → Reminders</strong>, where you can also see what was sent and when.</div>
        </div>
      )}

      {view === 'requests' ? (
        <Table head={['Person', clientWord, 'Asked', '']} loading={loading}
          empty={q ? 'Nobody matches your search.' : 'No open requests.'}
          rows={shownRequests.map(r => [
            <><div style={S.name}>{r.name || r.email}</div>{r.name && <div style={S.email}>{r.email}</div>}</>,
            r.groupLabel, <span style={{ fontSize: 12 }}>{formatDate(r.requestedAt)}</span>,
            shows('users.add') && (
              <div style={S.actions}>
                <button onClick={() => decide(r, 'approve')} disabled={!!busy} style={S.actionPrimaryGreen}>Approve</button>
                <button onClick={() => decide(r, 'decline')} disabled={!!busy} style={S.actionDanger}>Decline</button>
              </div>
            ),
          ])} />
      ) : view === 'senders' ? (
        <>
          <div style={S.notice}>
            <div>
              These people sent faxes by email in the last 90 days but don't have a Fax Portal account.
              <strong> Their faxes by email are refused</strong> (not sent, and not held) until they're added to a client.
              Each time, they get an email with a button to request access (at most once a day). Follow-up owners get this list every Monday.
            </div>
            {senders?.isSuperAdmin && (
              <div style={S.noticeActions}>
                <button onClick={() => remind(senderRows)} disabled={!!busy} style={S.actionBtn}
                  title="Email each person on this list a reminder with a Request access button">
                  {busy === 'remind:all' ? 'Sending…' : <><Emo e="📨" /> Email reminders now ({senderRows.filter(remindable).length})</>}
                </button>
                <button onClick={() => setPicking('now')} disabled={!!busy} style={S.actionBtn}><Emo e="✉️" /> Send the list to Admins…</button>
              </div>
            )}
            {senders?.isSuperAdmin && <FollowupOwners getToken={getToken} notify={notify} />}
            {senders?.isOwner && (
              <div style={S.weeklyRow}>
                <span><strong>You're a follow-up owner for this list.</strong> You get it every Monday at 9 AM ET until everyone on it has an account.</span>
              </div>
            )}
          </div>
          <Table head={[{ label: 'Email', key: 'email' }, { label: clientWord, key: 'account' }, { label: 'Faxes (90 days)', key: 'faxes' },
            { label: 'Last fax', key: 'lastFax' }, { label: 'Last reminder', key: 'lastReminder' }, '']}
            sort={sort} onSort={onSort} loading={loading}
            empty={q ? 'Nobody matches your search.' : 'Everyone who sends faxes by email has an account.'}
            rows={shownSenders.map(s => [
              <><div style={S.name}>{s.email}</div>
                {s.requestStatus === 'pending' && <span style={{ ...S.badge, ...S.badgeAlert }}>Asked for access</span>}
                {s.removed && <span style={{ ...S.badge, ...S.badgeOff }}>Access removed</span>}
                {s.heldCount > 0 && <span style={{ ...S.badge, ...S.badgeAlert }} title="Sent automatically if they get access before then">{s.heldCount} fax{s.heldCount === 1 ? '' : 'es'} on hold until {formatDate(s.heldUntil)}</span>}</>,
              s.groupLabel, s.faxes,
              <span style={{ fontSize: 12 }}>{formatDate(s.lastFaxAt)}</span>,
              <span style={{ fontSize: 12 }}>{formatDate(s.lastWarnedAt)}</span>,
              !s.removed && (canAdd || senders?.isSuperAdmin) && (
                <div style={S.actions}>
                  {senders?.isSuperAdmin && remindable(s) && (
                    <button onClick={() => remind([s])} disabled={!!busy} style={S.actionBtn} title="Email them a reminder with a Request access button">
                      {busy === `remind:${s.email}` ? 'Sending…' : 'Remind'}
                    </button>
                  )}
                  {canAdd && (
                    <button onClick={() => { setAdding({ email: s.email, name: '' }); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                      disabled={!!busy} style={S.actionBtn}>Add</button>
                  )}
                </div>
              ),
            ])} />
        </>
      ) : (
        <>
        {canBulk && (
          <div style={S.bulkBar}>
            <span style={S.bulkCount}>{picked.length ? `${picked.length} selected` : `${listUsers.length} in this list${shownUsers.length < listUsers.length ? ` · showing ${shownUsers.length}` : ''}`}</span>
            {shows('users.online') && (
              <button onClick={() => signOut(toSignOut)} disabled={!!busy || !toSignOut.length} style={toSignOut.length ? S.actionDanger : S.actionBtn}
                title={picked.length && !toSignOut.length ? 'None of the people you ticked are online, or they are not below you' : 'Tick people who are online'}>
                {busy === 'signout' ? 'Signing out…' : `Sign out${toSignOut.length ? ` (${toSignOut.length})` : ''}`}
              </button>
            )}
            {shows('users.export') && (
              <button onClick={downloadReport} disabled={!!busy || !listUsers.length} style={S.actionBtn}>
                {busy === 'report' ? 'Preparing…' : picked.length ? <><Emo e="⬇" /> Download {picked.length} selected (.xlsx)</> : <><Emo e="⬇" /> Download report (.xlsx)</>}
              </button>
            )}
          </div>
        )}
        <Table head={[
          ...(canBulk ? [<input type="checkbox" checked={allTicked} onChange={toggleAll} aria-label="Select everyone in this list" />] : []),
          { label: 'Person', key: 'name' }, { label: 'Role', key: 'role' },
          accountOptions.length > 1
            ? <select value={accountPick} onChange={e => setAccountPick(e.target.value)} aria-label={clientWord} style={S.thSelect}>
                <option value="" style={S.thOption}>{clientWord}: All</option>
                {accountOptions.map(a => <option key={a.id} value={a.id} style={S.thOption}>{a.name}</option>)}
              </select>
            : clientWord,
          ...(individualHere ? [] : ['Teams']),
          { label: 'Two-factor', key: 'twofa' }, { label: view === 'removed' ? 'Removed' : view === 'deactivated' ? 'Deactivated' : view === 'online' ? 'Online since' : 'Last active', key: 'when' }, '']}
          sort={sort} onSort={onSort} loading={loading}
          empty={q ? 'Nobody matches your search.' : {
            active: 'Nobody has access yet.', no2fa: 'Everyone has set up two-factor authentication.',
            never: 'Everyone has signed in at least once.', inactive: 'Everyone has used the portal in the last 30 days.',
            reset: 'Nobody is waiting for a two-factor reset.', removed: 'Nobody has been removed.', blocked: 'Nobody is blocked.',
            online: 'Nobody is signed in right now.',
          }[view]}
          rows={shownUsers.map(u => [
            ...(canBulk ? [<input type="checkbox" checked={selected.has(u.email)} onChange={() => toggle(u.email)} aria-label={`Select ${u.name || u.email}`} />] : []),
            <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
              {/* Profile picture (3.29.0): photo, icon, or first letter */}
              <Avatar avatar={u.avatar} name={u.name || u.email} size={34} style={{ marginTop: 2, ...(u.status === 'removed' ? { opacity: 0.5 } : {}) }} />
              <div style={{ minWidth: 0 }}>
              <div style={S.name}>{u.name || u.email}{u.online && <span style={S.onlineDot} title={`Online · last seen ${formatDate(u.onlineLastSeen)}`}> ● Online</span>}</div>
              {u.name && <div style={S.email}>{u.email}</div>}
              {u.secondaryEmail && <div style={S.meta} title="Secondary email">also {u.secondaryEmail}</div>}
              <div style={S.meta}>{u.hasSignIn ? `Signs in with ${u.signInWith.join(' or ') || '—'}` : 'Hasn\'t signed in yet'}</div>
              {u.inviteStatus === 'pending' && (
                <span style={{ ...S.badge, ...S.badgeInvite }} title={`Invite sent ${formatDate(u.inviteSentAt)}`}>
                  Invited{u.inviteExpiresAt ? ` · expires ${formatDay(u.inviteExpiresAt)}` : ''} · {METHOD_LABELS[u.inviteMethod] || ''}
                </span>
              )}
              {u.accessBlock && (
                <span style={{ ...S.badge, ...S.badgeBlocked }} title={`Blocked ${formatDate(u.accessBlock.at)}: ${u.accessBlock.count} failed faxes to ${formatFax(u.accessBlock.number)} in one day. Unblock after checking with them.`}>
                  🚫 Blocked · {u.accessBlock.count} failed faxes to {formatFax(u.accessBlock.number)}{u.accessBlock.held > 0 ? ` · ${u.accessBlock.held} held` : ''}{u.accessBlock.until ? ` · until ${formatDate(u.accessBlock.until)}` : ''}
                </span>
              )}
              {u.inviteStatus === 'expired' && (
                <span style={{ ...S.badge, ...S.badgeAlert }} title={`Invite sent ${formatDate(u.inviteSentAt)}`}>Invite expired {formatDay(u.inviteExpiresAt)}</span>
              )}
              </div>
            </div>,
            u.canChangeRole && shows('users.changeRole') && u.status === 'active' && roleChoices(u).length > 1 ? (
              <select value={u.role} disabled={!!busy} onChange={e => changeRole(u, e.target.value)}
                aria-label={`Role for ${u.name || u.email}`} style={S.roleSelect}>
                {!roleChoices(u).some(o => o.id === u.role) && <option value={u.role}>{u.roleLabel}</option>}
                {roleChoices(u).map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
              </select>
            ) : u.roleLabel,
            // Everyone in more than one account: each, with their role there
            (u.accounts || []).length > 1 && !here
              ? <>{u.accounts.map(a => <div key={a.id} style={a.id === u.group ? undefined : S.meta}>{a.name} · {a.roleLabel}</div>)}
                  {/* 3.49.10: in both a business and an individual client → the account their emailed faxes use when they don't answer */}
                  {info?.canSetEmailDefault && u.accounts.some(a => a.type === 'business') && u.accounts.some(a => a.type === 'individual') && (
                    <label style={{ ...S.meta, display: 'block', marginTop: 4 }}>Emailed faxes default:{' '}
                      <select value={u.emailDefaultAccount || u.accounts.find(a => a.type === 'business')?.id} disabled={!!busy}
                        onChange={async e => {
                          try { await apiCall(`/portal/users/${encodeURIComponent(u.email)}/email-default`, getToken, { method: 'PUT', body: JSON.stringify({ accountId: e.target.value }) });
                            notify({ bell: 'people', tone: 'success', title: `${u.name || u.email}: emailed faxes default to ${u.accounts.find(a => a.id === e.target.value)?.name}` }); await load(); }
                          catch (err) { notify({ tone: 'error', title: 'Couldn’t save it', detail: err.message }); }
                        }}
                        aria-label={`Default account for ${u.name || u.email}'s emailed faxes`} style={{ fontSize: 12 }}>
                        {u.accounts.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                      </select>
                    </label>
                  )}</>
              : (here ? ((u.accounts || []).find(a => a.id === here)?.name || u.groupLabel) : u.groupLabel),
            // Teams (3.19.1): their teams at each business client; changeable
            // here (like the role) by people who may manage teams
            ...(individualHere ? [] : [<TeamsCell u={here ? { ...u, accounts: (u.accounts || []).filter(a => a.id === here) } : u} options={info?.teamOptions || {}} getToken={getToken} notify={notify} onChanged={load} editable={u.status === 'active' && !!info?.canManageTeams} />]),
            <>
              {u.twoFactor
                ? <><span style={{ ...S.badge, ...S.badgeOn }}>On</span>
                    <div style={S.meta}>{u.recoveryCodesLeft} recovery code{u.recoveryCodesLeft === 1 ? '' : 's'} left</div></>
                : <><span style={{ ...S.badge, ...S.badgeOff }}>Not set up</span>
                    {u.twoFactorReminderAt && <div style={S.meta}>Reminded {formatDay(u.twoFactorReminderAt)}</div>}</>}
              {u.resetRequestedAt && (
                <div><span style={{ ...S.badge, ...S.badgeAlert }}
                  title={`${formatDate(u.resetRequestedAt)}${u.resetRequestedOf?.length ? ` · asked ${u.resetRequestedOf.join(', ')}` : ''}`}>
                  Reset requested {formatDay(u.resetRequestedAt)}
                </span></div>
              )}
            </>,
            view === 'deactivated'
              ? <span style={{ fontSize: 12 }}>{formatDate(u.removedAt)}<div style={S.meta}>Email address doesn't exist</div></span>
            : view === 'removed'
              ? <span style={{ fontSize: 12 }}>{formatDate(u.removedAt)}{u.removedBy && <div style={S.meta}>by {u.removedBy}</div>}</span>
              : view === 'online'
                ? <span style={{ fontSize: 12, whiteSpace: 'nowrap' }}>{formatDate(u.onlineSince)}{u.onlineDevices > 1 && <div style={S.meta}>{u.onlineDevices} devices</div>}</span>
                : <span style={{ fontSize: 12, whiteSpace: 'nowrap' }}>{formatDate(u.lastActiveAt)}</span>,
            // 3.39.2: the buttons sit in a fixed 2 × 2 grid, so each one is in
            // the same place on every row (View as · Password… / Reset 2FA ·
            // Remove); an empty place stays empty; anything extra goes below
            (() => {
              const signOutBtn = u.online && u.canSignOut && shows('users.online') &&
                <button key="out" onClick={() => signOut([u])} disabled={!!busy} style={S.actionDanger} title="Sign them out on every device">Sign out</button>;
              const viewBtn = u.canViewAs && !viewAs &&
                <button key="view" onClick={() => viewAsPerson(u)} disabled={!!busy} style={S.actionView} title="Use the portal as this person"><Emo e="👁" /> View as</button>;
              const inviteBtn = u.canInvite && shows('users.add') && u.status === 'active' && (u.inviteStatus || !u.hasSignIn) &&
                // Never invited (e.g. set up by the system): "Send invite" (3.10.2)
                <button key="invite" onClick={() => { setAdding({ email: u.email, name: u.name || '', mode: 'resend', first: !u.inviteStatus && !u.hasSignIn, method: u.inviteMethod || 'link' }); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                  disabled={!!busy} style={u.inviteStatus === 'expired' ? S.actionPrimary : S.actionBtn}>{!u.inviteStatus && !u.hasSignIn ? 'Send invite' : 'Resend invite'}</button>;
              const pwBtn = u.canResetPassword && shows('users.password') && u.status === 'active' && u.hasSignIn &&
                <button key="pw" onClick={() => setPwFor(u)} disabled={!!busy} title="Send a reset email, or set a temporary password" style={S.actionBtn}>Password…</button>;
              const tfaBtn = u.canResetTwoFactor && shows('users.reset2fa') && u.status === 'active' && u.twoFactor &&
                <button key="2fa" onClick={() => act(u, 'reset-2fa')} disabled={!!busy}
                  style={u.resetRequestedAt ? S.actionPrimary : S.actionBtn}>Reset 2FA</button>;
              const removeBtn = u.canChangeAccess && shows('users.remove') && u.status === 'active' &&
                <button key="remove" onClick={() => act(u, 'remove')} disabled={!!busy} style={S.actionDanger}>Remove</button>;
              const restoreBtn = u.canChangeAccess && shows('users.remove') && u.status === 'removed' &&
                <button key="restore" onClick={() => act(u, 'restore')} disabled={!!busy} style={S.actionBtn}
                  title={u.removedReason === 'email-not-found' ? 'Turn their access back on once their email address works again (it also lets our emails reach them again); then send an invite or a password reset' : undefined}>
                  {u.removedReason === 'email-not-found' ? 'Reactivate' : 'Give access'}</button>;
              // 3.57.9: blocked after repeated failed faxes
              const unblockBtn = u.accessBlock && u.canUnblock && shows('users.unblock') && u.status === 'active' &&
                <button key="unblock" onClick={() => act(u, 'unblock')} disabled={!!busy} style={S.actionPrimary} title="Restore their access after checking with them">Unblock</button>;
              // 3.62.13 (audit S1): one login shared with another client: only Fax Portal support changes it
              const sharedNote = u.sharedLogin && u.status === 'active' &&
                <span key="shared" style={{ fontSize: 11.5, color: 'var(--t-78909c)', gridColumn: '1 / -1' }} title="Their password, two-factor, invites and sign-outs are managed by Fax Portal support, because they also use the Fax Portal at another company.">Also at another company: Fax Portal support manages their sign-in</span>;
              const slots = [viewBtn, pwBtn || inviteBtn, tfaBtn || signOutBtn, removeBtn || restoreBtn];
              const extra = [unblockBtn, pwBtn && inviteBtn, tfaBtn && signOutBtn, sharedNote].filter(Boolean);
              if (!slots.some(Boolean) && !extra.length) return null;
              return (
                <div style={S.actionGrid}>
                  {slots.map((b, i) => b || <span key={`gap${i}`} aria-hidden="true" />)}
                  {extra}
                </div>
              );
            })(),
          ])} />
          {!loading && <Pager {...pager} label="people" />}
        </>
      )}
    </div>
  );
}

// Reset someone's password: the branded reset email (1 hour), or a new
// temporary password (72 hours) that's emailed to them and shown here once.
function PasswordDialog({ person, getToken, notify, onClose }) {
  const [method, setMethod] = useState('link');
  const [busy, setBusy]     = useState(false);
  const [err, setErr]       = useState('');
  const [result, setResult] = useState(null);   // { password, expiresAt }
  const [copied, setCopied] = useState(false);
  const who = person.name || person.email;

  useEffect(() => {
    const onKey = e => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  async function submit() {
    setErr(''); setBusy(true);
    try {
      const r = await resetUserPassword(person.email, method, getToken);
      if (method === 'temp') setResult(r);
      else { notify({ bell: 'people', tone: 'success', title: `Reset email sent to ${who}`, detail: 'The link in it works for 1 hour.' }); onClose(); }
    } catch (e) { setErr(e.message); }
    setBusy(false);
  }

  return (
    <div style={S.overlay} onMouseDown={e => { if (e.target === e.currentTarget && !result) onClose(); }}>
      <div style={S.modal} role="dialog" aria-modal="true" aria-labelledby="pw-title">
        <h2 id="pw-title" style={S.modalTitle}>Reset {who}'s password</h2>
        {!result ? (
          <>
            <fieldset style={S.methods}>
              <legend style={S.methodsLegend}>How?</legend>
              <label style={{ ...S.methodRow, ...(method === 'link' ? S.methodRowOn : {}) }}>
                <input type="radio" name="pw-method" checked={method === 'link'} onChange={() => setMethod('link')} />
                <span><strong>Email them a reset link</strong><span style={S.methodHelp}> They choose a new password. The link works for 1 hour.</span></span>
              </label>
              <label style={{ ...S.methodRow, ...(method === 'temp' ? S.methodRowOn : {}) }}>
                <input type="radio" name="pw-method" checked={method === 'temp'} onChange={() => setMethod('temp')} />
                <span><strong>Set a temporary password</strong><span style={S.methodHelp}> Emailed to them and shown to you once. They're signed out now and must choose their own at next sign-in. Valid for 72 hours.</span></span>
              </label>
            </fieldset>
            <p style={S.modalText}>Their two-factor authentication stays the same.</p>
            {err && <div role="alert" style={S.errorBanner}>{err}</div>}
            <div style={S.modalActions}>
              <button onClick={onClose} style={S.actionBtn}>Cancel</button>
              <button onClick={submit} disabled={busy} style={S.primaryBtn}>{busy ? 'Working…' : method === 'temp' ? 'Set temporary password' : 'Send reset email'}</button>
            </div>
          </>
        ) : (
          <>
            <p style={S.modalText}>We emailed this temporary password to {person.email}. If you pass it on yourself, please do it in person or by phone. It won't be shown again.</p>
            <div style={S.tempBox}>
              <code style={S.tempCode}>{result.password}</code>
              <button onClick={async () => { try { await navigator.clipboard.writeText(result.password); setCopied(true); } catch { /* ignore */ } }} style={S.actionBtn}>{copied ? '✓ Copied' : 'Copy'}</button>
            </div>
            <p style={S.modalText}>Valid until {formatDate(result.expiresAt)}.</p>
            <div style={S.modalActions}>
              <button onClick={onClose} style={S.primaryBtn}>Done</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// Super Admins pick which Admins, Managers and Super Admins get the list.
// Each person receives only their own group's senders (Super Admins: all).
// Follow-up owners (Super Admins): per group, choose which of the group's
// Admins own the follow-up. Nothing is sent to a group until owners are
// chosen; new owners get the list straight away, then every Monday, and a
// pop-up when they sign in.
function FollowupOwners({ getToken, notify }) {
  const [groups, setGroups]   = useState(null);
  const [editing, setEditing] = useState(null);   // group id
  const [picked, setPicked]   = useState([]);
  const [busy, setBusy]       = useState(false);
  const [err, setErr]         = useState('');

  const load = useCallback(() => {
    fetchFollowupOwners(getToken).then(r => setGroups(r.groups || [])).catch(e => setErr(e.message));
  }, [getToken]);
  useEffect(() => { load(); }, [load]);

  async function save(g) {
    setBusy(true);
    try {
      const r = await setFollowupOwners(g.id, picked, getToken);
      setEditing(null);
      notify({ bell: 'people', tone: 'success', title: r.owners.length ? `Follow-up for ${g.name} started` : `Follow-up for ${g.name} stopped`,
        detail: r.notified?.length ? `${r.notified.length} new owner${r.notified.length === 1 ? ' was' : 's were'} emailed the list. They'll get it every Monday and when they sign in.` : undefined });
      load();
    } catch (e) { notify({ tone: 'error', title: "Couldn't save the owners", detail: e.message }); }
    setBusy(false);
  }

  if (err) return <div role="alert" style={S.errorBanner}>{err}</div>;
  if (!groups) return null;
  return (
    <div style={S.weeklyRow}>
      <div style={{ flex: 1, minWidth: 260 }}>
        <strong>Follow-up owners.</strong> Nothing is sent to a group's Admins until you choose who owns the follow-up.
        Owners get this list straight away, then every Monday at 9 AM ET until everyone has an account, and a reminder when they sign in.
        {groups.map(g => {
          const names = g.owners.map(e => g.admins.find(a => a.email === e)?.name || e);
          return (
            <div key={g.id} style={S.ownerRow}>
              <span><strong>{g.name}:</strong> {names.length ? names.join(', ') : <em>not started</em>}</span>
              {editing === g.id ? (
                <span style={S.ownerEdit}>
                  {g.admins.length ? g.admins.map(a => (
                    <label key={a.email} style={S.pickRow}>
                      <input type="checkbox" checked={picked.includes(a.email)}
                        onChange={() => setPicked(p => (p.includes(a.email) ? p.filter(x => x !== a.email) : [...p, a.email]))} />
                      <span>{a.name} <span style={S.meta}>· {a.email}</span></span>
                    </label>
                  )) : <span style={S.meta}>This group has no Admins yet. Make someone an Admin first (Role in the Active list).</span>}
                  <span style={S.row}>
                    <button onClick={() => setEditing(null)} style={S.actionBtn}>Cancel</button>
                    {g.admins.length > 0 && <button onClick={() => save(g)} disabled={busy} style={S.primaryBtn}>{busy ? 'Saving…' : 'Save'}</button>}
                  </span>
                </span>
              ) : (
                <button onClick={() => { setEditing(g.id); setPicked(g.owners); }} style={S.actionBtn}>Choose owners…</button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Send the list once to the people picked
function RecipientsDialog({ getToken, busy, onSend, onClose }) {
  const [people, setPeople] = useState(null);
  const [picked, setPicked] = useState([]);
  const [err, setErr]       = useState('');

  useEffect(() => {
    fetchDigestRecipients(getToken)
      .then(r => {
        const list = r.data || [];
        setPeople(list);
        // Start with every group Admin and Manager ticked
        setPicked(list.filter(p => p.level !== 'super_admin').map(p => p.email));
      })
      .catch(e => setErr(e.message));
    const onKey = e => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [getToken, onClose]);

  const toggle = email => setPicked(p => (p.includes(email) ? p.filter(x => x !== email) : [...p, email]));
  const groups = [...new Set((people || []).map(p => p.groupLabel))];

  return (
    <div style={S.overlay} onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={S.modal} role="dialog" aria-modal="true" aria-labelledby="rcp-title">
        <h2 id="rcp-title" style={S.modalTitle}>Send the list now to…</h2>
        <p style={S.modalText}>
          Each person gets the people at their own client who send faxes by email without an account. Fax Portal support is copied on emails to Admins and Managers.
        </p>
        {err && <div role="alert" style={S.errorBanner}>{err}</div>}
        {!people && !err && <Loader size="sm" />}
        {people && !people.length && <div style={S.meta}>There are no Admins or Managers yet.</div>}
        <div style={S.pickScroll}>
          {groups.map(g => (
            <fieldset key={g} style={S.pickGroup}>
              <legend style={S.pickLegend}>{g}</legend>
              {people.filter(p => p.groupLabel === g).map(p => (
                <label key={p.email} style={S.pickRow}>
                  <input type="checkbox" checked={picked.includes(p.email)} onChange={() => toggle(p.email)} />
                  <span><strong>{p.name}</strong> <span style={S.meta}>· {p.roleLabel} · {p.email}</span></span>
                </label>
              ))}
            </fieldset>
          ))}
        </div>
        <div style={S.modalActions}>
          <button onClick={onClose} style={S.actionBtn}>Cancel</button>
          <button onClick={() => onSend(picked)} disabled={busy || !picked.length} style={{ ...S.primaryBtn, ...(picked.length ? {} : { opacity: 0.5 }) }}>
            {busy ? 'Sending…' : `Send to ${picked.length} ${picked.length === 1 ? 'person' : 'people'}`}
          </button>
        </div>
      </div>
    </div>
  );
}

// Sort a copy by value(row) (strings, numbers or ISO dates); empty values last
function sortRows(rows, value, dir) {
  if (!value) return rows;
  return [...rows].sort((a, b) => {
    const x = value(a), y = value(b);
    if ((x === '' || x == null) !== (y === '' || y == null)) return x === '' || x == null ? 1 : -1;
    return (x < y ? -1 : x > y ? 1 : 0) * dir;
  });
}

// head: strings / elements, or { label, key } for a column you can sort by
function Table({ head, rows, loading, empty, sort, onSort }) {
  return (
    <div style={S.tableWrap}>
      <table style={S.table}>
        <thead>
          <tr style={S.thead}>{head.map((h, i) => {
            if (h && typeof h === 'object' && h.key && onSort) {
              const on = sort?.key === h.key;
              return (
                <th key={i} style={S.th} aria-sort={on ? (sort.dir > 0 ? 'ascending' : 'descending') : 'none'}>
                  <button onClick={() => onSort(h.key)} style={S.sortBtn} title={`Sort by ${h.label}`}>
                    {h.label}<span aria-hidden="true" style={{ opacity: on ? 1 : 0.35, marginLeft: 4 }}>{on ? (sort.dir > 0 ? '▲' : '▼') : '↕'}</span>
                  </button>
                </th>
              );
            }
            return <th key={i} style={S.th}>{h || <span style={S.srOnly}>Actions</span>}</th>;
          })}</tr>
        </thead>
        <tbody>
          {loading ? <tr><td colSpan={head.length} style={S.emptyCell}><Loader label="Loading people…" /></td></tr>
            : !rows.length ? <tr><td colSpan={head.length} style={S.emptyCell}>{empty}</td></tr>
            : rows.map((cells, i) => (
              <tr key={i} style={{ ...S.tr, background: i % 2 === 0 ? 'var(--c-fafafa)' : 'var(--c-ffffff)' }}>
                {cells.map((c, j) => <td key={j} style={{ ...S.td, ...(j === cells.length - 1 ? { textAlign: 'right' } : {}) }}>{c}</td>)}
              </tr>
            ))}
        </tbody>
      </table>
    </div>
  );
}

// One person's teams at each of their business clients (3.19.1). With
// "Manage teams": a Change button opens tick boxes for that client's teams
// and the default team, saved with PUT /portal/teams/people/:email.
function TeamsCell({ u, options, getToken, notify, onChanged, editable }) {
  const [open, setOpen] = useState(null);   // the client being changed
  const [picked, setPicked] = useState([]);
  const [def, setDef] = useState('');
  const [busy, setBusy] = useState(false);
  const accs = (u.accounts || []).filter(a => (a.teams || []).length || options[a.id]);
  if (!accs.length) return <span style={S.meta}>—</span>;
  const multi = (u.accounts || []).length > 1;
  async function save(a) {
    setBusy(true);
    try {
      await apiCall(`/portal/teams/people/${encodeURIComponent(u.email)}?account=${encodeURIComponent(a.id)}`, getToken,
        { method: 'PUT', body: JSON.stringify({ teams: picked, defaultTeam: def || picked[0] || null }) });
      notify({ bell: 'people', tone: 'success', title: `${u.name || u.email}: teams saved` });
      setOpen(null);
      await onChanged();
    } catch (e) { notify({ tone: 'error', title: "Couldn't change the teams", detail: e.message }); }
    setBusy(false);
  }
  return accs.map(a => (
    <div key={a.id} style={{ marginBottom: 2 }}>
      {multi && <span style={S.meta}>{a.name}: </span>}
      {(a.teams || []).length ? a.teams.join(', ') : <span style={S.meta}>No team</span>}
      {editable && options[a.id] && open !== a.id && (
        <button onClick={() => { setOpen(a.id); setPicked(a.teamIds || []); setDef(a.defaultTeam || ''); }} style={S.teamsBtn}>Change</button>
      )}
      {open === a.id && (
        <div style={S.teamsBox}>
          {options[a.id].map(t => (
            <div key={t.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '3px 0' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, flex: 1, cursor: 'pointer' }}>
                <input type="checkbox" checked={picked.includes(t.id)}
                  onChange={() => setPicked(p => (p.includes(t.id) ? p.filter(x => x !== t.id) : [...p, t.id]))} />
                {t.name}
              </label>
              {picked.includes(t.id) && picked.length > 1 && (
                <label style={{ ...S.meta, display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }} title="Faxes they send by email go out on this team's number">
                  <input type="radio" name={`def-${u.email}-${a.id}`} checked={(def || picked[0]) === t.id} onChange={() => setDef(t.id)} /> default
                </label>
              )}
            </div>
          ))}
          <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
            <button disabled={busy} onClick={() => save(a)} style={S.teamsSave}>{busy ? 'Saving…' : 'Save'}</button>
            <button disabled={busy} onClick={() => setOpen(null)} style={S.teamsBtn}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  ));
}

const METHOD_LABELS = { link: 'password link', temp: 'temporary password', google: 'Google' };
const METHODS = [
  { id: 'link',   label: 'Email a link to set their password', help: 'Recommended. The link works once, for 72 hours.' },
  { id: 'temp',   label: 'Email a temporary password', help: 'Valid for 72 hours. They must choose their own password when they sign in.' },
  { id: 'google', label: 'They sign in with Google', help: 'For Google accounts. They get an email saying their account is ready.' },
];

// Add a person, approve an access request (mode 'approve') or resend an
// invite (mode 'resend'). Everyone picks how the person signs in first.
function AddPerson({ initial, domains, roleOptions, account, teamOptions = {}, defaultTeams = {}, getToken, notify, onAdded }) {
  // 3.19.11: Super Admins on All clients pick the client; business clients
  // with teams pick the new person's team (their default team first)
  const { effectiveInfo } = useAuth();
  const allClients = (effectiveInfo?.accounts || []);
  const [clientPick, setClientPick] = useState('');
  const clientId = account?.id || initial.group || clientPick || null;
  const clientObj = account || allClients.find(a => a.id === clientId) || null;
  const teamsHere = clientId ? (teamOptions[clientId] || []) : [];
  const [team, setTeam] = useState('');
  const teamValue = team && teamsHere.some(t => t.id === team) ? team : (defaultTeams[clientId] || teamsHere[0]?.id || '');
  const mode = initial.mode || 'add';
  const first = !!initial.first;   // resend mode, but never invited before
  const [email, setEmail]   = useState(initial.email || '');
  const [name, setName]     = useState(initial.name || '');
  const [role, setRole]     = useState('user');
  const [method, setMethod] = useState(initial.method || 'link');
  const [busy, setBusy]     = useState(false);
  const [err, setErr]       = useState('');
  // The account picked at the top (Super Admins with "All accounts": the
  // business account of the email domain, whose roles are the first set)
  const options = ((clientId ? roleOptions[clientId] : Object.values(roleOptions)[0]) || [])
    .filter(o => o.id !== 'super_admin' || isSuperAdminEmail(email));   // 3.19.13
  const individual = clientObj?.type === 'individual';
  const who = name.trim() || email.trim();

  async function submit(e) {
    e.preventDefault();
    setErr(''); setBusy(true);
    try {
      if (mode === 'resend') {
        await sendUserInvite(email, method, getToken);
        notify({ bell: 'people', tone: 'success', title: first ? `Invite sent to ${who}` : `New invite sent to ${who}`, detail: method === 'google' ? undefined : first ? 'It\'s valid for 72 hours.' : 'It\'s valid for 72 hours. The earlier one no longer works.' });
      } else if (mode === 'approve') {
        const r = await decideAccessRequest(email, 'approve', getToken, { role, method, ...(teamValue ? { team: teamValue } : {}) });
        notify({ bell: 'people', tone: 'success', title: `${who} was added`, detail: `We emailed them how to sign in.${r.heldSent ? ` ${r.heldSent} fax${r.heldSent === 1 ? '' : 'es'} on hold ${r.heldSent === 1 ? 'was' : 'were'} sent.` : ''}` });
      } else {
        const r = await addUser({ email: email.trim(), name: name.trim(), role, method, ...(clientPick && !account ? { account: clientPick } : {}), ...(teamValue ? { team: teamValue } : {}) }, getToken);
        if (r.joined) notify({ bell: 'people', tone: 'success', title: `${who} now also has access to ${account?.name || 'your company'}`, detail: 'They sign in as usual and pick the client at the top.' });
        else if (r.inviteError) notify({ tone: 'error', title: `${who} was added, but the invite email failed`, detail: 'Use "Resend invite" to try again.' });
        else notify({ bell: 'people', tone: 'success', title: r.restored ? `${who} has access again` : `${who} was added`, detail: `We emailed them how to sign in.${r.heldSent ? ` ${r.heldSent} fax${r.heldSent === 1 ? '' : 'es'} on hold ${r.heldSent === 1 ? 'was' : 'were'} sent.` : ''}` });
      }
      onAdded();
    } catch (e2) { setErr(e2.message); }
    setBusy(false);
  }

  return (
    <form onSubmit={submit} style={S.addBox}>
      {mode !== 'add' && (
        <div style={S.addTitle}>{mode === 'resend' ? (first ? `Send the invite to ${who}` : `Resend the invite to ${who}`) : `Approve ${who}'s request`}</div>
      )}
      <div style={S.addFields}>
        {mode === 'add' ? (
          <>
            <label style={S.label}>{individual ? 'Email' : 'Work email'}
              <input type="email" value={email} onChange={e => setEmail(e.target.value)} required autoFocus
                placeholder={!individual && domains[0] ? `name@${domains[0]}` : 'name@company.com'} style={S.input} />
            </label>
            <label style={S.label}>Name <span style={S.optional}>(optional)</span>
              <input value={name} onChange={e => setName(e.target.value)} maxLength={80} style={S.input} />
            </label>
          </>
        ) : (
          <div style={{ ...S.label, justifyContent: 'flex-end' }}><span style={S.fixedEmail}>{email}</span></div>
        )}
        {mode === 'add' && !account && allClients.length > 0 && (
          <label style={{ ...S.label, flex: '0 1 200px' }}>Client
            <select value={clientPick} onChange={e => { setClientPick(e.target.value); setTeam(''); setRole('user'); }} style={S.input}>
              <option value="">By email domain</option>
              {allClients.map(a => <option key={a.id} value={a.id}>{a.name}{a.type === 'individual' ? ' (Individual)' : ''}</option>)}
            </select>
          </label>
        )}
        {mode !== 'resend' && teamsHere.length > 0 && (
          <label style={{ ...S.label, flex: '0 1 180px' }}>Team
            <select value={teamValue} onChange={e => setTeam(e.target.value)} style={S.input}>
              {teamsHere.map(t => <option key={t.id} value={t.id}>{t.name}{t.id === defaultTeams[clientId] ? ' (default)' : ''}</option>)}
            </select>
          </label>
        )}
        {mode !== 'resend' && options.length > 1 && !individual && (
          <label style={{ ...S.label, flex: '0 1 180px' }}>Role
            <select value={role} onChange={e => setRole(e.target.value)} style={S.input}>
              {options.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
            </select>
          </label>
        )}
        {role === 'super_admin' && (
          <div role="note" style={{ ...S.errorBanner, flexBasis: '100%', margin: 0 }}>
            A Super Admin sees and changes everything at every client: people, roles, billing and other Super Admins.
          </div>
        )}
      </div>
      <fieldset style={S.methods}>
        <legend style={S.methodsLegend}>How will they sign in the first time?</legend>
        {METHODS.map(m => (
          <label key={m.id} style={{ ...S.methodRow, ...(method === m.id ? S.methodRowOn : {}) }}>
            <input type="radio" name="invite-method" checked={method === m.id} onChange={() => setMethod(m.id)} />
            <span><strong>{m.label}</strong><span style={S.methodHelp}> {m.help}</span></span>
          </label>
        ))}
      </fieldset>
      {err && <div role="alert" style={S.errorBanner}>{err}</div>}
      <div style={S.addActions}>
        <button type="submit" disabled={busy} style={S.primaryBtn}>
          {busy ? 'Sending…' : mode === 'resend' ? (first ? 'Send invite' : 'Send new invite') : mode === 'approve' ? 'Approve and send invite' : 'Add and send invite'}
        </button>
        {mode !== 'add' && <button type="button" onClick={onAdded} style={S.actionBtn}>Cancel</button>}
      </div>
      {mode === 'add' && (
        <p style={S.help}>
          {clientObj
            ? <>{individual && <>Individual client: the first person is its owner (Admin), the next its one staff member (User). </>}They join <strong>{clientObj.name}</strong>{!individual && account && domains.length ? ` (${domains.join(', ')} addresses)` : ''}{teamValue ? <>, team <strong>{teamsHere.find(t => t.id === teamValue)?.name}</strong></> : ''}. Someone who already has an account elsewhere in the portal is added to this one too. </>
            : <>They join the company for their email domain{domains.length ? ` (${domains.join(', ')})` : ''}, and its default team. Or pick the client above. </>}
          After signing in, they set up two-factor authentication.
        </p>
      )}
    </form>
  );
}

const btn = { padding: '6px 12px', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 600, fontFamily: 'inherit', whiteSpace: 'nowrap' };
const S = {
  topRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap', marginBottom: 16 },
  title: { margin: '0 0 4px', color: 'var(--t-00695c)', fontSize: 18, fontWeight: 700 },
  sub: { margin: 0, color: 'var(--t-607d8b)', fontSize: 13, lineHeight: 1.5, maxWidth: 640 },
  topActions: { display: 'flex', gap: 8 },
  primaryBtn: { padding: '8px 16px', background: 'var(--c-00897b)', color: 'var(--t-ffffff)', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600, fontFamily: 'inherit' },
  refreshBtn: { padding: '8px 16px', background: 'var(--c-37474f)', color: 'var(--t-ffffff)', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600, fontFamily: 'inherit' },
  addBox: { background: 'var(--c-ffffff)', border: '1px solid var(--c-e0f2f1)', borderRadius: 12, padding: '14px 18px', marginBottom: 16, boxShadow: '0 1px 6px rgb(var(--c-000000-rgb) / 0.05)' },
  addFields: { display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' },
  label: { display: 'flex', flexDirection: 'column', gap: 5, fontSize: 13, fontWeight: 600, color: 'var(--t-546e7a)', flex: '1 1 220px' },
  optional: { fontWeight: 400, color: 'var(--t-90a4ae)' },
  addTitle: { fontSize: 15, fontWeight: 700, color: 'var(--t-00695c)', marginBottom: 8 },
  fixedEmail: { fontSize: 15, fontWeight: 600, color: 'var(--t-263238)', padding: '9px 0' },
  methods: { border: '1px solid var(--c-eceff1)', borderRadius: 10, padding: '8px 12px', margin: '12px 0 0', display: 'flex', flexDirection: 'column', gap: 4 },
  methodsLegend: { fontSize: 12, fontWeight: 700, color: 'var(--t-546e7a)', padding: '0 4px' },
  methodRow: { display: 'flex', alignItems: 'flex-start', gap: 10, padding: '7px 8px', borderRadius: 8, fontSize: 13, color: 'var(--t-263238)', cursor: 'pointer', lineHeight: 1.45 },
  methodRowOn: { background: 'var(--c-f0faf8)' },
  methodHelp: { color: 'var(--t-78909c)', fontWeight: 400 },
  addActions: { display: 'flex', gap: 8, marginTop: 12 },
  badgeInvite: { background: 'var(--c-e3f2fd)', color: 'var(--t-0d47a1)' },
  ownerRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10, flexWrap: 'wrap', padding: '8px 0', borderTop: '1px solid var(--c-fff3c4)', marginTop: 6 },
  ownerEdit: { display: 'flex', flexDirection: 'column', gap: 4, minWidth: 240 },
  row: { display: 'flex', gap: 8, marginTop: 6 },
  weeklyRow: { display: 'flex', gap: 12, alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', paddingTop: 8, borderTop: '1px dashed var(--c-ffe082)' },
  tempBox: { display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px', background: 'var(--c-f1f8f6)', borderRadius: 10 },
  tempCode: { fontSize: 20, letterSpacing: '1.5px', fontFamily: 'monospace', color: 'var(--t-263238)', flex: 1 },
  input: { padding: '9px 12px', borderRadius: 8, border: '1.5px solid var(--c-e0e0e0)', fontSize: 14, color: 'var(--t-263238)', fontFamily: 'inherit' },
  help: { margin: '10px 0 0', fontSize: 12, color: 'var(--t-78909c)', lineHeight: 1.5 },
  filterRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 12 },
  views: { display: 'flex', gap: 6, flexWrap: 'wrap' },
  viewBtn: PILL,
  viewBtnActive: PILL_ON,
  viewBtnAlert: { border: '1.5px solid var(--c-ef6c00)', color: 'var(--t-e65100)' },
  count: { marginLeft: 4, fontWeight: 700 },
  search: { padding: '8px 12px', borderRadius: 8, border: '1.5px solid var(--c-e0e0e0)', fontSize: 13, minWidth: 220, fontFamily: 'inherit' },
  errorBanner: { background: 'var(--c-fce4ec)', color: 'var(--t-b71c1c)', padding: '10px 14px', borderRadius: 8, margin: '10px 0', fontSize: 13 },
  notice: { background: 'var(--c-fff8e1)', border: '1px solid var(--c-ffe082)', color: 'var(--t-5d4037)', borderRadius: 10, padding: '12px 16px', marginBottom: 12, fontSize: 13, lineHeight: 1.55, display: 'flex', flexDirection: 'column', gap: 10 },
  noticeActions: { display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' },
  dateLabel: { display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, fontWeight: 700, color: 'var(--t-6d4c41)' },
  dateInput: { padding: '7px 10px', borderRadius: 8, border: '1.5px solid var(--c-d7ccc8)', fontSize: 13, fontFamily: 'inherit' },
  tableWrap: { background: 'var(--c-ffffff)', borderRadius: 12, overflow: 'auto', boxShadow: '0 1px 6px rgb(var(--c-000000-rgb) / 0.07)', border: '1px solid var(--b-e8f5e9)' },
  table: { width: '100%', borderCollapse: 'collapse' },
  thead: { background: 'var(--c-00695c)' },
  th: { padding: '11px 14px', textAlign: 'left', color: 'var(--t-ffffff)', fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', whiteSpace: 'nowrap' },
  tr: { borderBottom: '1px solid var(--c-f0f0f0)' },
  td: { padding: '10px 14px', fontSize: 13, color: 'var(--t-333333)', verticalAlign: 'top' },
  name: { fontWeight: 600, color: 'var(--t-263238)', overflowWrap: 'anywhere' },
  email: { fontSize: 12, color: 'var(--t-607d8b)', overflowWrap: 'anywhere' },
  meta: { fontSize: 11, color: 'var(--t-90a4ae)', marginTop: 3 },
  roleSelect: { padding: '5px 8px', borderRadius: 6, border: '1.5px solid var(--c-cfd8dc)', fontSize: 13, fontFamily: 'inherit' },
  badge: { display: 'inline-block', padding: '2px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap', marginTop: 3 },
  badgeOn: { background: 'var(--c-e8f5e9)', color: 'var(--t-1b5e20)' },
  badgeOff: { background: 'var(--c-eceff1)', color: 'var(--t-546e7a)' },
  badgeAlert: { background: 'var(--c-fff3e0)', color: 'var(--t-e65100)' },
  badgeBlocked: { background: 'var(--c-ffebee)', color: 'var(--t-b71c1c)', fontWeight: 600 },
  actions: { display: 'flex', gap: 6, justifyContent: 'flex-end', flexWrap: 'wrap' },
  // 3.39.2: a person's buttons, each always in the same place, all the same width
  actionGrid: { display: 'grid', gridTemplateColumns: 'repeat(2, 112px)', gap: 6, justifyContent: 'end' },
  actionBtn: { ...btn, background: 'var(--c-ffffff)', color: 'var(--t-37474f)', border: '1.5px solid var(--c-cfd8dc)' },
  actionView: { ...btn, background: 'var(--c-ffffff)', color: 'var(--t-e65100)', border: '1.5px solid var(--c-ffcc80)' },
  actionPrimary: { ...btn, background: 'var(--c-ef6c00)', color: 'var(--t-ffffff)', border: '1.5px solid var(--c-ef6c00)' },
  actionPrimaryGreen: { ...btn, background: 'var(--c-00897b)', color: 'var(--t-ffffff)', border: '1.5px solid var(--c-00897b)' },
  actionDanger: { ...btn, background: 'var(--c-ffffff)', color: 'var(--t-b71c1c)', border: '1.5px solid var(--c-ef9a9a)' },
  thSelect: TH_SELECT,
  teamsBtn: { marginLeft: 6, background: 'none', border: '1px solid var(--c-cfd8dc)', borderRadius: 6, padding: '1px 8px', fontSize: 11, color: 'var(--t-546e7a)', cursor: 'pointer', fontFamily: 'inherit' },
  teamsSave: { background: 'var(--c-00897b)', color: 'var(--t-ffffff)', border: 'none', borderRadius: 6, padding: '4px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' },
  teamsBox: { marginTop: 6, padding: '8px 10px', border: '1px solid var(--c-e0f2f1)', borderRadius: 8, background: 'var(--c-ffffff)', fontSize: 13, minWidth: 180 },
  thOption: TH_OPTION,
  showGroup: { display: 'flex', alignItems: 'center', gap: 6 },
  showLabel: { fontSize: 13, color: 'var(--t-607d8b)', fontWeight: 600 },
  showSelect: { padding: '8px 10px', borderRadius: 8, border: '1.5px solid var(--c-e0e0e0)', fontSize: 13, fontFamily: 'inherit', background: 'var(--c-ffffff)' },
  sortBtn: { background: 'none', border: 'none', color: 'inherit', font: 'inherit', textTransform: 'inherit', letterSpacing: 'inherit', cursor: 'pointer', padding: 0, whiteSpace: 'nowrap' },
  bulkBar: { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', margin: '0 0 8px' },
  bulkCount: { fontSize: 13, color: 'var(--t-607d8b)', marginRight: 4 },
  onlineDot: { fontSize: 11, fontWeight: 700, color: 'var(--t-2e7d32)', marginLeft: 6, whiteSpace: 'nowrap' },
  emptyCell: { textAlign: 'center', padding: '36px 0', color: 'var(--t-90a4ae)', fontSize: 14 },
  overlay: { position: 'fixed', inset: 0, background: 'rgb(var(--c-0f2823-rgb) / 0.45)', zIndex: 1200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 },
  modal: { background: 'var(--c-ffffff)', borderRadius: 14, width: 'min(560px, 100%)', maxHeight: '85vh', display: 'flex', flexDirection: 'column', gap: 10, padding: '20px 22px', boxShadow: '0 20px 60px rgb(var(--c-000000-rgb) / 0.25)' },
  modalTitle: { margin: 0, fontSize: 19, fontWeight: 700, color: 'var(--t-00695c)' },
  modalText: { margin: 0, fontSize: 13, color: 'var(--t-607d8b)', lineHeight: 1.5 },
  pickScroll: { overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 10 },
  pickGroup: { border: '1px solid var(--c-eceff1)', borderRadius: 10, padding: '8px 12px', margin: 0 },
  pickLegend: { fontSize: 11, fontWeight: 800, color: 'var(--t-00695c)', textTransform: 'uppercase', letterSpacing: '0.5px', padding: '0 4px' },
  pickRow: { display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0', fontSize: 14, color: 'var(--t-263238)', cursor: 'pointer' },
  modalActions: { display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 4 },
  srOnly: { position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' },
};
